// Policy management: current policies list, add/edit dialog, delete (Calyvra admin),
// and a complete Policy History timeline across coverage years by Athena ID.
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { ShieldCheck, Plus, Pencil, Trash2, Loader2, History, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

export const POLICY_STATUS_COLORS = {
  Active: 'bg-green-100 text-green-700',
  Pending: 'bg-amber-100 text-amber-700',
  Superseded: 'bg-slate-200 text-slate-600',
  Terminated: 'bg-red-100 text-red-700',
  Cancelled: 'bg-orange-100 text-orange-700',
};

const emptyPolicy = {
  insurance_carrier: '', member_id: '', plan_name: '', market_type: '', state: '',
  effective_date: '', end_date: '', monthly_premium: '', aptc_amount: '',
  final_member_responsibility: '', policy_status: 'Active', notes: '', agent_id: '', product_type: '',
};

const money = (v) => (v || v === 0
  ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—');

export function PolicyPanel({ caseId, athenaId, canEdit, constants, workspaceKey }) {
  const [policies, setPolicies] = useState([]);
  const [canDelete, setCanDelete] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPolicy);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => { portalApi.getAgents().then(setAgents).catch(() => {}); }, []);

  // Product types allowed for this workspace (e.g. Latinos Salud → on/off-exchange health only).
  const productTypeOptions = (constants?.workspace_product_types?.[workspaceKey]) || constants?.product_types || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [polRes, histRes] = await Promise.allSettled([
        portalApi.getPolicies(caseId),
        athenaId ? portalApi.getPolicyHistory(athenaId) : Promise.resolve({ years: [] }),
      ]);
      if (polRes.status === 'fulfilled') {
        setPolicies(polRes.value.policies || []);
        setCanDelete(polRes.value.can_delete || false);
      } else {
        toast.error(polRes.reason?.message || 'Could not load policies');
      }
      setHistory(histRes.status === 'fulfilled' ? (histRes.value.years || []) : []);
    } finally {
      setLoading(false);
    }
  }, [caseId, athenaId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyPolicy); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      insurance_carrier: p.insurance_carrier || '', member_id: p.member_id || '',
      plan_name: p.plan_name || '', market_type: p.market_type || '', state: p.state || '',
      effective_date: p.effective_date || '', end_date: p.end_date || '',
      monthly_premium: p.monthly_premium ?? '', aptc_amount: p.aptc_amount ?? '',
      final_member_responsibility: p.final_member_responsibility ?? '',
      policy_status: p.policy_status || 'Active', notes: p.notes || '', agent_id: p.agent_id || '', product_type: p.product_type || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        market_type: form.market_type || null,
        state: form.state || null,
        monthly_premium: form.monthly_premium !== '' ? parseFloat(form.monthly_premium) : null,
        aptc_amount: form.aptc_amount !== '' ? parseFloat(form.aptc_amount) : null,
        final_member_responsibility: form.final_member_responsibility !== '' ? parseFloat(form.final_member_responsibility) : null,
        agent_id: form.agent_id || null,
        product_type: form.product_type || null,
      };
      if (editing) {
        await portalApi.updatePolicy(caseId, editing.id, payload);
        toast.success('Policy updated');
      } else {
        await portalApi.createPolicy(caseId, payload);
        toast.success('Policy added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await portalApi.deletePolicy(caseId, deleteTarget.id);
      toast.success('Policy deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="policy-panel">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-500" />
            <h3 className="font-semibold text-slate-900">Policies</h3>
            <span className="text-sm text-slate-400">({policies.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-policy-history">
              <History className="h-4 w-4 mr-1" /> {showHistory ? 'Hide' : 'View'} Policy History
            </Button>
            {canEdit && (
              <Button size="sm" onClick={openCreate} data-testid="add-policy-btn"><Plus className="h-4 w-4 mr-1" /> Add Policy</Button>
            )}
          </div>
        </div>

        {policies.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="no-policies">No policies yet. {canEdit && 'Click "Add Policy" to create one.'}</p>
        ) : (
          <div className="space-y-3">
            {policies.map((p) => (
              <div key={p.id} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors" data-testid={`policy-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{p.insurance_carrier || 'Unnamed carrier'}</span>
                      <Badge className={POLICY_STATUS_COLORS[p.policy_status] || 'bg-slate-100 text-slate-700'} data-testid={`policy-status-${p.id}`}>{p.policy_status}</Badge>
                      {p.state && <Badge variant="outline" className="text-slate-600">{p.state}</Badge>}
                      {p.product_type && <Badge variant="outline" className="text-indigo-600 border-indigo-200">{p.product_type}</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{p.plan_name || '—'}{p.member_id ? ` · Member ${p.member_id}` : ''}{p.market_type ? ` · ${p.market_type}` : ''}</p>
                    {p.agent_name && <p className="text-xs text-sky-600 mt-0.5" data-testid={`policy-agent-${p.id}`}>Agent: {p.agent_name}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-policy-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`delete-policy-${p.id}`} title="Delete (Calyvra admin only)"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                  <div><p className="text-slate-400 text-xs">Effective</p><p className="text-slate-700">{p.effective_date || '—'}</p></div>
                  <div><p className="text-slate-400 text-xs">End / Term</p><p className="text-slate-700">{p.end_date || '—'}</p></div>
                  <div><p className="text-slate-400 text-xs">Monthly</p><p className="text-slate-700">{money(p.monthly_premium)}</p></div>
                  <div><p className="text-slate-400 text-xs">Member Resp.</p><p className="text-slate-700">{money(p.final_member_responsibility)}</p></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showHistory && (
        <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="policy-history">
          <div className="flex items-center gap-2 mb-5">
            <Clock className="h-5 w-5 text-sky-500" />
            <h3 className="font-semibold text-slate-900">Policy History — Athena ID {athenaId}</h3>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400">No policy history across coverage years.</p>
          ) : (
            <div className="space-y-6">
              {history.map((yr) => (
                <div key={yr.coverage_year_id || yr.coverage_year_label} data-testid={`history-year-${yr.coverage_year_label}`}>
                  <p className="text-sm font-semibold text-slate-800 mb-2">{yr.coverage_year_label}</p>
                  <div className="border-l-2 border-slate-200 pl-4 space-y-2">
                    {yr.policies.map((p) => (
                      <div key={p.id} className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-700 font-medium">{p.insurance_carrier || 'Unnamed carrier'}</span>
                        {p.state && <span className="text-slate-400">· {p.state}</span>}
                        <Badge className={POLICY_STATUS_COLORS[p.policy_status] || 'bg-slate-100 text-slate-700'}>{p.policy_status}</Badge>
                        {p.effective_date && <span className="text-xs text-slate-400">from {p.effective_date}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Policy' : 'Add Policy'}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Insurance Carrier</Label><Input value={form.insurance_carrier} onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} data-testid="policy-form-carrier" /></div>
            <div className="space-y-2"><Label>Member ID</Label><Input value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} /></div>
            <div className="space-y-2"><Label>Plan Name</Label><Input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Product Type</Label>
              <Select value={form.product_type || 'none'} onValueChange={(v) => setForm({ ...form, product_type: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="policy-form-product"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{productTypeOptions.map(pt => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>State</Label>
              <Select value={form.state || 'none'} onValueChange={(v) => setForm({ ...form, state: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="policy-form-state"><SelectValue placeholder="Select state (optional)" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{constants?.us_states?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Market Type</Label>
              <Select value={form.market_type || 'none'} onValueChange={(v) => setForm({ ...form, market_type: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select...</SelectItem>{constants?.market_types?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Policy Status</Label>
              <Select value={form.policy_status} onValueChange={(v) => setForm({ ...form, policy_status: v })}>
                <SelectTrigger data-testid="policy-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{constants?.policy_statuses?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
            {agents.length > 0 && (
              <div className="space-y-2"><Label>Agent / Producer</Label>
                <Select value={form.agent_id || 'none'} onValueChange={(v) => setForm({ ...form, agent_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="policy-form-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Unassigned —</SelectItem>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>End / Termination Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Monthly Premium ($)</Label><Input type="number" value={form.monthly_premium} onChange={(e) => setForm({ ...form, monthly_premium: e.target.value })} /></div>
            <div className="space-y-2"><Label>APTC ($)</Label><Input type="number" value={form.aptc_amount} onChange={(e) => setForm({ ...form, aptc_amount: e.target.value })} /></div>
            <div className="space-y-2"><Label>Member Responsibility ($)</Label><Input type="number" value={form.final_member_responsibility} onChange={(e) => setForm({ ...form, final_member_responsibility: e.target.value })} /></div>
            <div className="space-y-2 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          {!editing && form.policy_status === 'Active' && (
            <p className="text-xs text-amber-600" data-testid="supersede-note">Any existing Active policy will automatically be marked "Superseded" and preserved in history.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-policy-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Policy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-policy-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy?</AlertDialogTitle>
            <AlertDialogDescription>
              Only delete a policy that was created by mistake. This action is recorded in the audit log.
              Policy: <strong>{deleteTarget?.insurance_carrier}</strong> ({deleteTarget?.policy_status}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-policy-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PolicyPanel;
